"use client";

type PaletteSwatch = {
  hex?: string;
  population?: number;
  rgb?: number[];
};

type PaletteLike = Record<string, PaletteSwatch | null | undefined>;

const LIGHT_AURORA_SWATCHES = ["LightVibrant", "Vibrant", "LightMuted"] as const;
const DARK_AURORA_SWATCHES = ["Vibrant", "DarkVibrant", "Muted"] as const;

const toTriplet = (hexes: string[]) => {
  if (hexes.length >= 3) return [hexes[0], hexes[1], hexes[2]];
  if (hexes.length === 2) return [hexes[0], hexes[1], hexes[0]];
  if (hexes.length === 1) return [hexes[0], hexes[0], hexes[0]];
  return null;
};

export const pickTopSwatchHexes = (palette: PaletteLike, count: number) => {
  const swatches = Object.values(palette).filter(
    (swatch): swatch is PaletteSwatch => Boolean(swatch?.hex)
  );
  const sorted = swatches.sort(
    (a, b) => (b.population ?? 0) - (a.population ?? 0)
  );
  const uniqueHexes = Array.from(new Set(sorted.map((swatch) => swatch.hex!)));

  return uniqueHexes.slice(0, count);
};

export const pickNamedSwatchHexes = (
  palette: PaletteLike,
  names: readonly string[]
) =>
  Array.from(
    new Set(
      names
        .map((name) => palette[name]?.hex)
        .filter((hex): hex is string => Boolean(hex))
    )
  );

export const getAuroraPaletteHexes = (
  palette: PaletteLike,
  mode: "light" | "dark"
) => {
  const preferredSwatches =
    mode === "light" ? LIGHT_AURORA_SWATCHES : DARK_AURORA_SWATCHES;
  const preferredHexes = pickNamedSwatchHexes(palette, preferredSwatches);
  const preferredTriplet = toTriplet(preferredHexes);

  if (preferredTriplet) {
    return preferredTriplet;
  }

  const fallbackHexes = pickTopSwatchHexes(palette, 3);
  return toTriplet(fallbackHexes);
};

export const getPreferredOverlaySwatch = (
  palette: PaletteLike,
  mode: "light" | "dark"
) => {
  const preferredSwatches =
    mode === "light"
      ? ["LightVibrant", "Vibrant", "LightMuted", "Muted"]
      : ["Vibrant", "DarkVibrant", "Muted", "DarkMuted"];

  for (const name of preferredSwatches) {
    const swatch = palette[name];
    if (swatch?.rgb?.length === 3) {
      return swatch;
    }
  }

  const populatedSwatches = Object.values(palette).filter(
    (swatch): swatch is PaletteSwatch =>
      Boolean(swatch?.rgb?.length === 3 && swatch.hex)
  );

  return (
    populatedSwatches.sort(
      (a, b) => (b.population ?? 0) - (a.population ?? 0)
    )[0] ?? null
  );
};
